import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import api, { partService, truckService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { MODULES } from '../auth/roles';
import { confirmOdometerUpdate } from '../utils/odometer';
import { formatPortalDate } from '../utils/dateFormat';
import { ServiceInventoryPartOption, ServicePartThumbnail, serviceInventoryPartSearchText } from './ServiceInventoryPartOption';
import ServiceLogAttachments from './ServiceLogAttachments';
import ServiceAttentionNotice from './ServiceAttentionNotice';
import ServiceLogInventoryEditPicker from './ServiceLogInventoryEditPicker';

function formatDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return formatPortalDate(d);
}

function truckLabel(t) {
  if (!t) return '—';
  return t.name || [t.year, t.make, t.model].filter(Boolean).join(' ') || `Truck #${t.id}`;
}

function truckSearchText(t) {
  return [
    truckLabel(t),
    t?.license_plate,
    t?.plate,
    t?.vin,
    t?.serial_number,
    t?.make,
    t?.model,
    t?.year,
    t?.status,
  ].filter(Boolean).join(' ').toLowerCase();
}

function toDateInputValue(d) {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function inventoryPartId(part) {
  return Number(part?.representative_id || part?.id);
}

function inventoryPartLabel(part) {
  return [
    part?.part_type || part?.description || 'Part',
    part?.sku || part?.biomed_part_number_sku || part?.part_number_oem,
    part?.location,
  ].filter(Boolean).join(' • ');
}

function clampTextSx(lines) {
  return {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    whiteSpace: 'pre-wrap',
  };
}

function MaintenanceCompletedSelect({ idPrefix, items, value, onChange }) {
  return (
    <Grid item xs={12}>
      <FormControl fullWidth size="small">
        <InputLabel id={`${idPrefix}-label`}>Scheduled Maintenance Completed</InputLabel>
        <Select
          labelId={`${idPrefix}-label`}
          multiple
          value={value}
          label="Scheduled Maintenance Completed"
          onChange={(event) => onChange(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {selected.map((id) => {
                const item = items.find((candidate) => String(candidate.id) === String(id));
                return <Chip key={id} size="small" label={item?.name || `#${id}`} />;
              })}
            </Box>
          )}
        >
          {items.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              <Checkbox checked={value.some((id) => String(id) === String(item.id))} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.name}</Typography>
                <Typography variant="caption" color="text.secondary">{item.category}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        Saving synchronizes the next maintenance date and odometer target for this vehicle.
      </Typography>
    </Grid>
  );
}

export default function AllServiceLogs() {
  const navigate = useNavigate();
  const { hasModuleAccess } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [trucks, setTrucks] = useState([]);
  const [inventoryParts, setInventoryParts] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [q, setQ] = useState('');
  const [truckId, setTruckId] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortBy, setSortBy] = useState('service_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [form, setForm] = useState({
    truck_id: '',
    service_date: toDateInputValue(new Date()),
    odometer: '',
    service_done: '',
    performed_by: '',
    repairs_needed: '',
    notes: '',
  });
  const [selectedInventoryPartIds, setSelectedInventoryPartIds] = useState([]);
  const [inventoryQuantities, setInventoryQuantities] = useState({});
  const [selectedConsumableIds, setSelectedConsumableIds] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailRecord, setDetailRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    truck_id: '',
    service_date: '',
    odometer: '',
    service_done: '',
    performed_by: '',
    repairs_needed: '',
    notes: '',
  });
  const [editUsedInventoryParts, setEditUsedInventoryParts] = useState([]);
  const [editRestoreInventoryPartIds, setEditRestoreInventoryPartIds] = useState([]);
  const [editSelectedInventoryPartIds, setEditSelectedInventoryPartIds] = useState([]);
  const [editInventoryQuantities, setEditInventoryQuantities] = useState({});
  const [editInventoryLoading, setEditInventoryLoading] = useState(false);
  const [editConsumableIds, setEditConsumableIds] = useState([]);

  const loadConsumables = async (selectedTruckId) => {
    if (!selectedTruckId) {
      setConsumables([]);
      return [];
    }
    const response = await truckService.getConsumables(selectedTruckId);
    const rows = (response.data?.rows || []).filter((item) => item.active !== false);
    setConsumables(rows);
    return rows;
  };

  useEffect(() => {
    let ignore = false;
    Promise.all([
      truckService.getAll(),
      partService.getInventory().catch(() => ({ data: [] })),
    ])
      .then(([truckRes, inventoryRes]) => {
        if (ignore) return;
        const data = truckRes.data?.data || truckRes.data || [];
        setTrucks(Array.isArray(data) ? data : []);
        setInventoryParts((Array.isArray(inventoryRes.data) ? inventoryRes.data : [])
          .filter((part) => Number(part.quantity ?? 0) > 0 && inventoryPartId(part)));
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const params = useMemo(() => ({
    page,
    pageSize,
    q: q || undefined,
    truckId: truckId === 'all' ? undefined : truckId,
    from: from || undefined,
    to: to || undefined,
    sortBy,
    sortDir,
  }), [page, pageSize, q, truckId, from, to, sortBy, sortDir]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await truckService.getAllServiceLogs(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to load service logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.pageSize, params.q, params.truckId, params.from, params.to, params.sortBy, params.sortDir]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  const truckNameById = useMemo(() => {
    const map = new Map();
    (trucks || []).forEach((t) => map.set(t.id, truckLabel(t)));
    return map;
  }, [trucks]);

  const activeTruckLabel = useMemo(() => {
    if (truckId === 'all') return 'All trucks';
    const id = parseInt(String(truckId), 10);
    const found = (trucks || []).find((t) => t.id === id);
    return found ? truckLabel(found) : `Truck #${truckId}`;
  }, [truckId, trucks]);

  const activeDateLabel = useMemo(() => {
    if (!from && !to) return 'Any date';
    if (from && !to) return `From ${from}`;
    if (!from && to) return `Until ${to}`;
    return `${from} → ${to}`;
  }, [from, to]);

  const sortLabel = useMemo(() => {
    const by =
      sortBy === 'truck' ? 'Truck'
        : sortBy === 'performed_by' ? 'Performed By'
          : sortBy === 'created_at' ? 'Created'
            : 'Service Date';
    return `${by} (${sortDir === 'asc' ? 'A→Z' : 'Z→A'})`;
  }, [sortBy, sortDir]);

  const handleReset = () => {
    setQ('');
    setTruckId('all');
    setFrom('');
    setTo('');
    setSortBy('service_date');
    setSortDir('desc');
    setPage(0);
    setPageSize(10);
  };

  const openAddDialog = () => {
    setAddError('');
    const defaultTruckId = truckId !== 'all' ? truckId : '';
    const defaultTruck = defaultTruckId
      ? (trucks || []).find((truck) => String(truck.id) === String(defaultTruckId))
      : null;
    setForm({
      truck_id: defaultTruckId,
      service_date: toDateInputValue(new Date()),
      odometer: defaultTruck?.odometer ?? '',
      service_done: '',
      performed_by: '',
      repairs_needed: '',
      notes: '',
    });
    setSelectedInventoryPartIds([]);
    setInventoryQuantities({});
    setSelectedConsumableIds([]);
    loadConsumables(defaultTruckId).catch(() => setConsumables([]));
    setAddOpen(true);
  };

  const closeAddDialog = () => {
    if (addSaving) return;
    setAddOpen(false);
    setAddError('');
  };

  const setFormField = (field, value) => {
    if (field === 'truck_id') {
      setSelectedConsumableIds([]);
      loadConsumables(value).catch(() => setConsumables([]));
    }
    setForm((prev) => {
      if (field !== 'truck_id') return { ...prev, [field]: value };
      const nextTruck = (trucks || []).find((truck) => String(truck.id) === String(value));
      return {
        ...prev,
        truck_id: value,
        odometer: nextTruck?.odometer ?? '',
      };
    });
  };

  const selectedTruck = useMemo(() => (
    (trucks || []).find((truck) => String(truck.id) === String(form.truck_id)) || null
  ), [form.truck_id, trucks]);

  const selectedInventoryParts = useMemo(() => (
    selectedInventoryPartIds
      .map((id) => inventoryParts.find((part) => Number(inventoryPartId(part)) === Number(id)))
      .filter(Boolean)
  ), [inventoryParts, selectedInventoryPartIds]);
  const editSelectedInventoryParts = useMemo(() => (
    editSelectedInventoryPartIds
      .map((id) => inventoryParts.find((part) => Number(inventoryPartId(part)) === Number(id)))
      .filter(Boolean)
  ), [editSelectedInventoryPartIds, inventoryParts]);

  const handleInventorySelectionChange = (value) => {
    const ids = (typeof value === 'string' ? value.split(',') : value).map((item) => Number(item));
    setSelectedInventoryPartIds(ids);
    setInventoryQuantities((prev) => {
      const next = {};
      ids.forEach((id) => {
        next[id] = prev[id] || 1;
      });
      return next;
    });
  };

  const handleEditInventorySelectionChange = (parts) => {
    const ids = parts.map((part) => Number(inventoryPartId(part)));
    setEditSelectedInventoryPartIds(ids);
    setEditInventoryQuantities((previous) => {
      const next = {};
      ids.forEach((id) => {
        next[id] = previous[id] || 1;
      });
      return next;
    });
  };

  const reloadInventory = async () => {
    try {
      const res = await partService.getInventory();
      setInventoryParts((Array.isArray(res.data) ? res.data : [])
        .filter((part) => Number(part.quantity ?? 0) > 0 && inventoryPartId(part)));
    } catch (_) {}
  };

  const handleAddServiceLog = async () => {
    if (!form.truck_id) {
      setAddError('Select a vehicle.');
      return;
    }
    if (!form.service_date || !String(form.service_done || '').trim()) {
      setAddError('Service Date and Service Done are required.');
      return;
    }
    if (selectedConsumableIds.length && !String(form.odometer ?? '').trim()) {
      setAddError('Odometer is required when completing scheduled maintenance reminders.');
      return;
    }

    try {
      setAddSaving(true);
      setAddError('');
      if (!await confirmOdometerUpdate({
        currentValue: selectedTruck?.odometer,
        nextValue: form.odometer,
        context: 'update the vehicle odometer from this service log entry',
      })) {
        setAddSaving(false);
        return;
      }

      await truckService.createServiceLog(form.truck_id, {
        service_date: form.service_date,
        service_done: String(form.service_done || '').trim(),
        odometer: form.odometer || null,
        performed_by: form.performed_by || null,
        repairs_needed: form.repairs_needed || null,
        notes: form.notes || null,
        consumable_ids: selectedConsumableIds,
        inventory_parts: selectedInventoryPartIds.map((id) => ({
          part_id: id,
          quantity: Number(inventoryQuantities[id] || 1),
        })),
      });

      setAddOpen(false);
      setPage(0);
      await Promise.all([load(), reloadInventory()]);
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('vehiclesUpdated'));
    } catch (e) {
      console.error(e);
      setAddError(e?.response?.data?.error || e?.message || 'Failed to add service log.');
    } finally {
      setAddSaving(false);
    }
  };

  const openServiceDetails = async (serviceId) => {
    if (!serviceId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailRecord(null);
    try {
      const response = await api.get(`/service-logs/${serviceId}/details`);
      setDetailRecord(response.data || null);
    } catch (e) {
      setDetailError(e?.response?.data?.error || e?.message || 'Failed to load service log details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const editSelectedTruck = useMemo(() => (
    (trucks || []).find((truck) => String(truck.id) === String(editForm.truck_id)) || null
  ), [editForm.truck_id, trucks]);

  const setEditFormField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const openEditDialog = async (wrapped) => {
    const service = wrapped?.service || wrapped;
    if (!service?.id || !service?.truck_id) return;
    setEditError('');
    setEditRecord(service);
    setEditForm({
      truck_id: String(service.truck_id),
      service_date: toDateInputValue(service.service_date),
      odometer: service.odometer ?? '',
      service_done: service.service_done || '',
      performed_by: service.performed_by || '',
      repairs_needed: service.repairs_needed || '',
      notes: service.notes || '',
    });
    setEditUsedInventoryParts([]);
    setEditRestoreInventoryPartIds([]);
    setEditSelectedInventoryPartIds([]);
    setEditInventoryQuantities({});
    setEditConsumableIds([]);
    setEditOpen(true);

    try {
      setEditInventoryLoading(true);
      const [detailsResponse] = await Promise.all([
        api.get(`/service-logs/${service.id}/details`),
        loadConsumables(service.truck_id),
      ]);
      setEditUsedInventoryParts(Array.isArray(detailsResponse.data?.used_inventory_parts) ? detailsResponse.data.used_inventory_parts : []);
      setEditConsumableIds(Array.isArray(detailsResponse.data?.consumable_ids) ? detailsResponse.data.consumable_ids : []);
    } catch (e) {
      setEditError(e?.response?.data?.error || e?.message || 'Failed to load linked maintenance and inventory details.');
    } finally {
      setEditInventoryLoading(false);
    }
  };

  const closeEditDialog = (force = false) => {
    if (editSaving && force !== true) return;
    setEditOpen(false);
    setEditError('');
    setEditRecord(null);
    setEditUsedInventoryParts([]);
    setEditRestoreInventoryPartIds([]);
    setEditSelectedInventoryPartIds([]);
    setEditInventoryQuantities({});
    setEditConsumableIds([]);
    setEditInventoryLoading(false);
  };

  const toggleEditRestorePart = (installedPartIds = []) => {
    const ids = installedPartIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    setEditRestoreInventoryPartIds((prev) => {
      const current = new Set(prev);
      const allSelected = ids.length > 0 && ids.every((id) => current.has(id));
      ids.forEach((id) => {
        if (allSelected) current.delete(id);
        else current.add(id);
      });
      return Array.from(current);
    });
  };

  const handleSaveEdit = async () => {
    if (!editRecord?.id || !editForm.truck_id) return;
    if (!editForm.service_date || !String(editForm.service_done || '').trim()) {
      setEditError('Service Date and Service Done are required.');
      return;
    }
    if (editConsumableIds.length && !String(editForm.odometer ?? '').trim()) {
      setEditError('Odometer is required when completing scheduled maintenance reminders.');
      return;
    }

    try {
      setEditSaving(true);
      setEditError('');
      if (!await confirmOdometerUpdate({
        currentValue: editSelectedTruck?.odometer,
        nextValue: editForm.odometer,
        context: 'update the vehicle odometer from this service log edit',
      })) {
        setEditSaving(false);
        return;
      }

      await truckService.updateServiceLog(editForm.truck_id, editRecord.id, {
        service_date: editForm.service_date,
        service_done: String(editForm.service_done || '').trim(),
        odometer: editForm.odometer === '' ? null : editForm.odometer,
        performed_by: editForm.performed_by || null,
        repairs_needed: editForm.repairs_needed || null,
        notes: editForm.notes || null,
        consumable_ids: editConsumableIds,
        restore_inventory_part_ids: editRestoreInventoryPartIds,
        inventory_parts: editSelectedInventoryPartIds.map((id) => ({
          part_id: id,
          quantity: Number(editInventoryQuantities[id] || 1),
        })),
      });

      await Promise.all([load(), reloadInventory()]);
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('vehiclesUpdated'));
      closeEditDialog(true);
    } catch (e) {
      console.error(e);
      setEditError(e?.response?.data?.error || e?.message || 'Failed to update service log.');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', p: { xs: 1.25, sm: 1.5, md: 2 } }}>
      <Card className="portal-page-header"
        sx={{
          mb: 1.25,
          borderRadius: 2.25,
          overflow: 'hidden',
          boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)',
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.2px', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                Service Logs
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25, fontWeight: 700 }}>
                All service logs across all vehicles
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`${total} records`} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white' }} />
                <Chip size="small" icon={<FilterAltIcon sx={{ color: 'white !important' }} />} label={activeTruckLabel} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white' }} />
                <Chip size="small" label={activeDateLabel} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white' }} />
                <Chip size="small" icon={<SortIcon sx={{ color: 'white !important' }} />} label={sortLabel} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white' }} />
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
                sx={{ bgcolor: '#00e096', color: '#001f3f', fontWeight: 900, borderRadius: 2, '&:hover': { bgcolor: '#19f5aa' } }}
              >
                Add Service Log
              </Button>
              <Box
                sx={{
                  width: { xs: 40, sm: 46 },
                  height: { xs: 40, sm: 46 },
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.14)',
                  boxShadow: '0 3px 14px rgba(0,0,0,0.14)',
                }}
              >
                <BuildIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: 'white' }} />
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,31,63,0.12)', boxShadow: '0 8px 28px rgba(0,0,0,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#001f3f' }}>
            Filters
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
              Reset
            </Button>
            <Button variant="contained" onClick={() => load()} sx={{ borderRadius: 2 }}>
              Apply
            </Button>
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={q}
              onChange={(e) => { setPage(0); setQ(e.target.value); }}
              placeholder="Service done / performed by / notes / truck"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'rgba(0,31,63,0.55)', mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Truck</InputLabel>
              <Select value={truckId} label="Truck" onChange={(e) => { setPage(0); setTruckId(e.target.value); }}>
                <MenuItem value="all">All</MenuItem>
                {(trucks || []).map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {truckLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={from}
              onChange={(e) => { setPage(0); setFrom(e.target.value); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={to}
              onChange={(e) => { setPage(0); setTo(e.target.value); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={(e) => { setPage(0); setSortBy(e.target.value); }}>
                <MenuItem value="service_date">Service Date</MenuItem>
                <MenuItem value="truck">Truck</MenuItem>
                <MenuItem value="performed_by">Performed By</MenuItem>
                <MenuItem value="created_at">Created</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select value={sortDir} label="Direction" onChange={(e) => { setPage(0); setSortDir(e.target.value); }}>
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Per Page</InputLabel>
              <Select value={pageSize} label="Per Page" onChange={(e) => { setPage(0); setPageSize(parseInt(String(e.target.value), 10)); }}>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Paper sx={{ mt: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,31,63,0.12)' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f' }}>Truck</TableCell>
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f', width: 130 }}>Service Date</TableCell>
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f' }}>Service Done</TableCell>
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f', width: 150 }}>Inventory Parts</TableCell>
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f', width: 160 }}>Performed By</TableCell>
                  {!isPhone && <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f' }}>Repairs Needed</TableCell>}
                  {!isPhone && <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f' }}>Notes</TableCell>}
                  <TableCell sx={{ fontWeight: 900, bgcolor: '#f7f9fc', color: '#001f3f', width: 140 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isPhone ? 6 : 8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isPhone ? 6 : 8}>
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                        No matching records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((wrapped) => {
                    const service = wrapped?.service || wrapped;
                    const truck = wrapped?.truck || null;
                    const label = truck ? truckLabel(truck) : (truckNameById.get(service?.truck_id) || `Truck #${service?.truck_id}`);
                    const plate = truck?.license_plate || '';
                    const repairsText = service?.repairs_needed || '—';
                    const notesText = service?.notes || '—';

                    return (
                      <TableRow
                        key={service?.id}
                        hover
                        onClick={() => openServiceDetails(service?.id)}
                        sx={{
                          cursor: 'pointer',
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 31, 63, 0.02)' },
                          transition: 'background-color 120ms ease',
                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.06)' },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 800 }}>
                          {label}
                          {plate ? (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {plate}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{formatDate(service?.service_date)}</TableCell>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Tooltip title={service?.service_done || ''} disableHoverListener={!(service?.service_done || '').length}>
                            <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {service?.service_done || '—'}
                            </Typography>
                          </Tooltip>
                          {isPhone && (repairsText !== '—' || notesText !== '—') ? (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ...clampTextSx(2) }}>
                              {repairsText !== '—' ? `Repairs: ${repairsText}` : `Notes: ${notesText}`}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {Number(wrapped?.inventory_part_count || 0) > 0 ? (
                            <Chip
                              size="small"
                              icon={<Inventory2Icon />}
                              label={`${Number(wrapped.inventory_part_count)} used`}
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 800 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">None recorded</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {service?.performed_by || '—'}
                          </Typography>
                        </TableCell>
                        {!isPhone && (
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Tooltip title={repairsText} placement="top-start" disableHoverListener={String(repairsText).length < 40}>
                              <Typography variant="body2" sx={clampTextSx(3)}>
                                {repairsText}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        )}
                        {!isPhone && (
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Tooltip title={notesText} placement="top-start" disableHoverListener={String(notesText).length < 40}>
                              <Typography variant="body2" sx={clampTextSx(3)}>
                                {notesText}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<VisibilityIcon />}
                              onClick={() => openServiceDetails(service?.id)}
                              sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                            >
                              Details
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => openEditDialog(wrapped)}
                              sx={{ borderRadius: 2, whiteSpace: 'nowrap', borderColor: '#001f3f', color: '#001f3f', fontWeight: 800 }}
                            >
                              Edit
                            </Button>
                            {hasModuleAccess(MODULES.VEHICLES) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNewIcon />}
                              onClick={() => navigate(`/truck/${service?.truck_id}`)}
                              sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                            >
                              Vehicle
                            </Button>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 700 }}>
            Page {page + 1} of {Math.max(1, totalPages)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <Button variant="outlined" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 6 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#e8f5e9', color: '#087f5b' }}>
            <BuildIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Service Log Details</Typography>
            <Typography variant="caption" color="text.secondary">Service information and inventory parts used</Typography>
          </Box>
          <IconButton aria-label="Close service details" onClick={() => setDetailOpen(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f7f9fc' }}>
          {detailLoading && (
            <Box sx={{ py: 7, display: 'grid', placeItems: 'center', gap: 1.5 }}>
              <CircularProgress />
              <Typography color="text.secondary">Loading service and inventory details...</Typography>
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {!detailLoading && detailRecord && (() => {
            const service = detailRecord.service || {};
            const truck = detailRecord.truck || {};
            const usedParts = Array.isArray(detailRecord.used_inventory_parts) ? detailRecord.used_inventory_parts : [];
            return (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider flexItem orientation={isPhone ? 'horizontal' : 'vertical'} />}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                      <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{truckLabel(truck)}</Typography>
                      {truck.license_plate && <Typography variant="caption" color="text.secondary">Plate: {truck.license_plate}</Typography>}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Service date</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatDate(service.service_date)}</Typography>
                      {service.odometer != null && <Typography variant="caption" color="text.secondary">Odometer: {Number(service.odometer).toLocaleString()} km</Typography>}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Performed by</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{service.performed_by || '—'}</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">Service completed</Typography>
                  <Typography sx={{ fontWeight: 800, whiteSpace: 'pre-wrap' }}>{service.service_done || '—'}</Typography>
                  {(service.repairs_needed || service.notes) && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
                      <Box><Typography variant="caption" color="text.secondary">Repairs needed</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{service.repairs_needed || '—'}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Notes</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{service.notes || '—'}</Typography></Box>
                    </Box>
                  )}
                </Paper>

                <ServiceLogAttachments
                  serviceLogId={service.id}
                  initialImages={detailRecord.repair_images || []}
                  onChange={(repairImages) => setDetailRecord((current) => current ? { ...current, repair_images: repairImages } : current)}
                />

                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                    <Inventory2Icon sx={{ color: '#087f5b' }} />
                    <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Inventory Parts Used</Typography>
                    <Chip size="small" color={usedParts.length ? 'success' : 'default'} label={`${usedParts.reduce((sum, part) => sum + Number(part.quantity || 0), 0)} total`} />
                  </Stack>
                  {usedParts.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2.5, textAlign: 'center' }}>
                      <Inventory2Icon sx={{ fontSize: 36, color: 'text.disabled' }} />
                      <Typography sx={{ mt: 0.5, fontWeight: 700 }}>No inventory parts were recorded for this service.</Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1.5}>
                      {usedParts.map((part) => (
                        <Paper key={part.sku || part.installed_part_ids?.[0]} variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: 'rgba(8,127,91,0.25)' }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{part.part_type || 'Inventory Part'}</Typography>
                                <Chip size="small" label={`Qty ${part.quantity}`} color="success" />
                                <Chip size="small" label={part.condition || '—'} variant="outlined" />
                              </Stack>
                              <Typography variant="body2" sx={{ mt: 0.75 }}><strong>SKU:</strong> {part.sku || '—'}</Typography>
                              <Typography variant="body2"><strong>BioMed SKU:</strong> {part.biomed_part_number_sku || '—'} &nbsp; <strong>OEM:</strong> {part.part_number_oem || '—'}</Typography>
                              <Typography variant="body2"><strong>Make / Model:</strong> {[part.make, part.model].filter(Boolean).join(' ') || '—'} &nbsp; <strong>Serial:</strong> {part.serial_number || '—'}</Typography>
                            </Box>
                          </Stack>
                          {Array.isArray(part.photos) && part.photos.length > 0 && (
                            <Box sx={{ mt: 1.75 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <PhotoLibraryIcon fontSize="small" color="primary" />
                                <Typography variant="caption" sx={{ fontWeight: 800 }}>{part.photos.length} part photo{part.photos.length === 1 ? '' : 's'}</Typography>
                              </Stack>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1 }}>
                                {part.photos.map((photo, index) => (
                                  <Box
                                    key={photo.id || photo.url || index}
                                    component="a"
                                    href={photo.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ display: 'block', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,31,63,0.14)', bgcolor: 'white', aspectRatio: '4 / 3' }}
                                  >
                                    <Box component="img" src={photo.url} alt={photo.name || `${part.sku} part`} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {detailRecord?.service && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => {
                const service = detailRecord.service;
                setDetailOpen(false);
                openEditDialog({ service, truck: detailRecord.truck, inventory_part_count: detailRecord.used_inventory_parts?.length || 0 });
              }}
            >
              Edit
            </Button>
          )}
          {detailRecord?.service?.truck_id && hasModuleAccess(MODULES.VEHICLES) && (
            <Button startIcon={<OpenInNewIcon />} onClick={() => navigate(`/truck/${detailRecord.service.truck_id}`)}>Open Vehicle</Button>
          )}
          <Button variant="contained" onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Edit Service Log
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
            Update service details and return any inventory parts added by mistake.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle"
                value={editSelectedTruck ? truckLabel(editSelectedTruck) : `Truck #${editForm.truck_id || ''}`}
                InputProps={{ readOnly: true }}
                helperText="Vehicle cannot be changed from edit. Open the vehicle if the log was entered under the wrong truck."
              />
            </Grid>
            {form.truck_id && (
              <Grid item xs={12}>
                <ServiceAttentionNotice
                  truckId={form.truck_id}
                  onOpenServiceLog={(serviceLogId) => {
                    setAddOpen(false);
                    openServiceDetails(serviceLogId);
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Service Date *"
                type="date"
                value={editForm.service_date}
                onChange={(e) => setEditFormField('service_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Odometer"
                type="number"
                value={editForm.odometer}
                onChange={(e) => setEditFormField('odometer', e.target.value)}
                placeholder="e.g., 125450"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Service Done *"
                value={editForm.service_done}
                onChange={(e) => setEditFormField('service_done', e.target.value)}
                placeholder="e.g., Changed oil filter, grease job, brake service"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Performed By"
                value={editForm.performed_by}
                onChange={(e) => setEditFormField('performed_by', e.target.value)}
                placeholder="Mechanic / Shop name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Repairs Needed (optional)"
                value={editForm.repairs_needed}
                onChange={(e) => setEditFormField('repairs_needed', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                value={editForm.notes}
                onChange={(e) => setEditFormField('notes', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            {(editInventoryLoading || editUsedInventoryParts.length > 0) && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fbff', borderColor: '#cfe0ff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
                    Inventory parts used in this service
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Select any part added by mistake. Saving will return it to inventory and remove it from this service log.
                  </Typography>
                  {editInventoryLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading used parts...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {editUsedInventoryParts.map((part) => {
                        const installedIds = Array.isArray(part.installed_part_ids) ? part.installed_part_ids : [];
                        const checked = installedIds.length > 0 && installedIds.every((id) => editRestoreInventoryPartIds.includes(Number(id)));
                        return (
                          <Paper
                            key={`${part.sku || part.part_type}-${installedIds.join('-')}`}
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: checked ? '#ecfdf5' : 'white', borderColor: checked ? '#66bb6a' : '#d8e2ef' }}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleEditRestorePart(installedIds)}
                              disabled={!installedIds.length}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f' }}>
                                {part.sku || 'No SKU'} • {part.part_type || 'Part'} • Qty {part.quantity || installedIds.length || 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                OEM: {part.part_number_oem || 'N/A'} • {checked ? 'Will be returned to inventory' : 'Keep attached to this service log'}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <ServiceLogInventoryEditPicker
                inventoryParts={inventoryParts}
                selectedParts={editSelectedInventoryParts}
                quantities={editInventoryQuantities}
                onSelectionChange={handleEditInventorySelectionChange}
                onQuantityChange={(id, value) => setEditInventoryQuantities((previous) => ({ ...previous, [id]: value }))}
              />
            </Grid>
            <MaintenanceCompletedSelect
              idPrefix="all-service-edit-maintenance"
              items={consumables}
              value={editConsumableIds}
              onChange={setEditConsumableIds}
            />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditDialog} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={editSaving ? <CircularProgress size={18} color="inherit" /> : <EditIcon />}
            onClick={handleSaveEdit}
            disabled={editSaving}
            sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
          >
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={closeAddDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Add Service Log
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
            Create a service entry and optionally consume parts from Shelf, Solvent Shed, Shop, or Parts Truck inventory.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={trucks || []}
                value={selectedTruck}
                autoHighlight
                openOnFocus
                clearOnEscape
                getOptionLabel={(option) => truckLabel(option)}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                filterOptions={(options, state) => {
                  const tokens = String(state.inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
                  if (!tokens.length) return options;
                  return options.filter((truck) => {
                    const haystack = truckSearchText(truck);
                    return tokens.every((token) => haystack.includes(token));
                  });
                }}
                onChange={(_event, truck) => setFormField('truck_id', truck?.id ? String(truck.id) : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle *"
                    placeholder="Search name, plate, VIN, make, model..."
                    helperText={selectedTruck ? [
                      selectedTruck.license_plate ? `Plate: ${selectedTruck.license_plate}` : null,
                      [selectedTruck.year, selectedTruck.make, selectedTruck.model].filter(Boolean).join(' '),
                    ].filter(Boolean).join(' • ') : 'Start typing to find the vehicle quickly.'}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ color: 'rgba(0,31,63,0.55)', mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, truck) => (
                  <Box component="li" {...props} key={truck.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f' }}>
                      {truckLabel(truck)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        truck.license_plate ? `Plate: ${truck.license_plate}` : null,
                        truck.vin ? `VIN: ${truck.vin}` : null,
                        [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
                      ].filter(Boolean).join(' • ') || `Vehicle #${truck.id}`}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Service Date *"
                type="date"
                value={form.service_date}
                onChange={(e) => setFormField('service_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Odometer"
                type="number"
                value={form.odometer}
                onChange={(e) => setFormField('odometer', e.target.value)}
                placeholder="e.g., 125450"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Service Done *"
                value={form.service_done}
                onChange={(e) => setFormField('service_done', e.target.value)}
                placeholder="e.g., Changed oil filter, grease job, brake service"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Performed By"
                value={form.performed_by}
                onChange={(e) => setFormField('performed_by', e.target.value)}
                placeholder="Mechanic / Shop name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Repairs Needed (optional)"
                value={form.repairs_needed}
                onChange={(e) => setFormField('repairs_needed', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                value={form.notes}
                onChange={(e) => setFormField('notes', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fbff' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>
                  Parts Used From Inventory
                </Typography>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={inventoryParts}
                  value={selectedInventoryParts}
                  autoHighlight
                  openOnFocus
                  disableCloseOnSelect
                  getOptionLabel={(part) => inventoryPartLabel(part)}
                  isOptionEqualToValue={(option, value) => Number(inventoryPartId(option)) === Number(inventoryPartId(value))}
                  filterOptions={(options, state) => {
                    const tokens = String(state.inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
                    if (!tokens.length) return options;
                    return options.filter((part) => tokens.every((token) => serviceInventoryPartSearchText(part).includes(token)));
                  }}
                  onChange={(_event, parts) => handleInventorySelectionChange(parts.map((part) => inventoryPartId(part)))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search inventory parts used"
                      placeholder="Type SKU, OEM, serial, type, location..."
                      helperText={`${inventoryParts.length.toLocaleString()} available • ${selectedInventoryParts.length} selected`}
                    />
                  )}
                  renderOption={(props, part, { selected }) => {
                    const id = inventoryPartId(part);
                    return (
                      <ServiceInventoryPartOption optionProps={{ ...props, key: id }} part={part} selected={selected} label={inventoryPartLabel(part)} />
                    );
                  }}
                />
                {selectedInventoryParts.length > 0 && (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {selectedInventoryParts.map((part) => {
                      const id = inventoryPartId(part);
                      const available = Math.max(1, Number(part.quantity ?? 1));
                      return (
                        <Grid item xs={12} md={6} key={id}>
                          <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', gap: 1.25, alignItems: 'center', borderRadius: 2 }}>
                            <ServicePartThumbnail part={part} size={58} />
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label={`Quantity used: ${part.part_type || part.sku || `Part #${id}`}`}
                              value={inventoryQuantities[id] || 1}
                              inputProps={{ min: 1, max: available }}
                              helperText={`Available: ${available.toLocaleString()} • ${part.location || part.source_locations?.join?.(', ') || 'Location unavailable'}`}
                              onChange={(e) => {
                                const value = Math.max(1, Math.min(available, Number(e.target.value || 1)));
                                setInventoryQuantities((prev) => ({ ...prev, [id]: value }));
                              }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Saving moves selected units from Shelf, Solvent Shed, Shop, or Parts Truck inventory into the selected vehicle and records movement history automatically.
                </Typography>
              </Paper>
            </Grid>
            <MaintenanceCompletedSelect
              idPrefix="all-service-add-maintenance"
              items={consumables}
              value={selectedConsumableIds}
              onChange={setSelectedConsumableIds}
            />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAddDialog} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={addSaving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            onClick={handleAddServiceLog}
            disabled={addSaving}
            sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
          >
            {addSaving ? 'Saving...' : 'Save Service Log'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
